import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import * as VisuallyHiddenPrimitive from "@radix-ui/react-visually-hidden"
import { ChevronLeft } from "lucide-react"

import { cn } from "../../lib/utils"

function findDialogTitleInChildren(children) {
  return React.Children.toArray(children).some((child) => {
    if (!React.isValidElement(child)) return false
    const childType = child.type
    if (childType === DialogPrimitive.Title) return true
    if (childType?.displayName === DialogPrimitive.Title.displayName) return true
    if (childType?.displayName === 'DialogTitle' || childType?.name === 'DialogTitle') return true
    if (childType?.displayName === 'FullScreenModalTitle' || childType?.name === 'FullScreenModalTitle') return true
    if (child.props?.children) return findDialogTitleInChildren(child.props.children)
    return false
  })
}

const FullScreenModal = ({ modal = false, ...props }) => (
  <DialogPrimitive.Root modal={modal} {...props} />
)

const FullScreenModalTrigger = DialogPrimitive.Trigger

const FullScreenModalPortal = DialogPrimitive.Portal

const FullScreenModalClose = DialogPrimitive.Close

let scrollLockCount = 0
let savedOverflow = ''

function lockBodyScroll() {
  if (scrollLockCount === 0) {
    savedOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  scrollLockCount++
}

function unlockBodyScroll() {
  scrollLockCount = Math.max(0, scrollLockCount - 1)
  if (scrollLockCount === 0) {
    document.body.style.overflow = savedOverflow
  }
}

const FullScreenModalOverlay = React.forwardRef(({ className, style, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "fixed z-[90] bg-black/40 animate-in fade-in-0 duration-200",
      className
    )}
    style={{
      top: 'calc(56px + env(safe-area-inset-top, 0px))',
      bottom: 'var(--app-tab-bar-height)',
      left: 0,
      right: 0,
      ...style
    }}
    {...props} />
))
FullScreenModalOverlay.displayName = "FullScreenModalOverlay"

const preventOutsideDismiss = (e) => e.preventDefault()

const FullScreenModalContent = React.forwardRef(({ className, children, style, onPointerDownOutside, onInteractOutside, ...props }, ref) => {
  const childList = React.Children.toArray(children)
  const hasTitle = findDialogTitleInChildren(childList)

  React.useEffect(() => {
    lockBodyScroll()
    return () => unlockBodyScroll()
  }, [])

  return (
    <FullScreenModalPortal>
      <FullScreenModalOverlay />
      <DialogPrimitive.Content
        ref={ref}
        aria-describedby={undefined}
        onPointerDownOutside={onPointerDownOutside ?? preventOutsideDismiss}
        onInteractOutside={onInteractOutside ?? preventOutsideDismiss}
        className={cn(
          "fixed inset-x-0 bottom-0 z-[90] w-full min-w-0 max-w-full overflow-x-hidden bg-background shadow-lg duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom safe-area-inset-bottom",
          className
        )}
        style={{
          height: 'calc(100dvh - 56px - env(safe-area-inset-top, 0px) - var(--app-tab-bar-height))',
          bottom: 'var(--app-tab-bar-height)',
          top: 'calc(56px + env(safe-area-inset-top, 0px))',
          ...style
        }}
        {...props}>
        <div className="flex justify-center pt-2 pb-0">
          <div className="h-1 w-8 rounded-full bg-muted-foreground/25" />
        </div>
        {!hasTitle && (
          <VisuallyHiddenPrimitive.Root>
            <DialogPrimitive.Title>Панель</DialogPrimitive.Title>
          </VisuallyHiddenPrimitive.Root>
        )}
        {children}
      </DialogPrimitive.Content>
    </FullScreenModalPortal>
  )
})
FullScreenModalContent.displayName = DialogPrimitive.Content.displayName

const FullScreenModalHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex min-w-0 items-center justify-between gap-2 overflow-x-hidden border-b px-4 pb-3 pt-4", className)}
    {...props} />
)
FullScreenModalHeader.displayName = "FullScreenModalHeader"

const FullScreenModalTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("min-w-0 flex-1 text-center text-lg font-semibold leading-none tracking-tight break-words", className)}
    {...props} />
))
FullScreenModalTitle.displayName = "FullScreenModalTitle"

const FullScreenModalBackButton = React.forwardRef(({ className, onClick, ...props }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    className={cn(
      "flex items-center justify-center w-11 h-11 rounded-full hover:bg-accent active:bg-accent/80 transition-colors -ml-2 touch-manipulation",
      className
    )}
    style={{ minWidth: '44px', minHeight: '44px' }}
    {...props}>
    <ChevronLeft className="h-6 w-6" />
  </button>
))
FullScreenModalBackButton.displayName = "FullScreenModalBackButton"

const FullScreenModalBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-4", className)}
    style={{
      minHeight: '0',
      WebkitOverflowScrolling: 'touch',
      touchAction: 'pan-y',
      overscrollBehavior: 'contain',
      paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))'
    }}
    {...props} />
))
FullScreenModalBody.displayName = "FullScreenModalBody"

export {
  FullScreenModal,
  FullScreenModalPortal,
  FullScreenModalOverlay,
  FullScreenModalTrigger,
  FullScreenModalClose,
  FullScreenModalContent,
  FullScreenModalHeader,
  FullScreenModalTitle,
  FullScreenModalBackButton,
  FullScreenModalBody,
}
